import { z } from "zod";
import { normalizeReportRange } from "@/core/lib/reportDateRange";
import {
	DEFAULT_DISBURSEMENT_STATUSES,
	defaultPayoutsReportSearch,
} from "./payoutsReportQueryOptions";

/** Every filter falls back to "not set" (`""`) instead of failing the route,
 *  so a stale or hand-edited URL still opens the report. */
const payoutsReportSearchSchema = z.object({
	dateFrom: z.string().catch(""),
	dateTo: z.string().catch(""),
	dateBasis: z
		.enum(["processedAt", "createdAt", "paymentDate"])
		.catch("processedAt"),
	page: z.number().int().min(1).catch(1),
	pageSize: z.number().int().min(1).max(200).catch(20),
	/** `""` (all), a group id, or `INDEPENDENT_GROUP` (no group). */
	groupId: z.string().catch(""),
	propertyId: z.string().catch(""),
	payoutProviderId: z.string().catch(""),
	legType: z.enum(["", "HotelPayout", "PlatformFees", "ExtraFees"]).catch(""),
	currency: z.string().catch(""),
	disbursementType: z.enum(["", "Booking", "Cancellation"]).catch(""),
	/** `None` asks for legs whose provider reports no settlement. */
	settlementStatus: z
		.enum(["", "Initiated", "Submitted", "Failed", "None"])
		.catch(""),
	/** Absent or empty (old URLs) = the default selection, Settled. */
	disbursementStatus: z
		.array(
			z.enum(["Settled", "Pending", "InProgress", "ManualReview", "Failed"]),
		)
		.catch([])
		.transform((statuses) =>
			statuses.length > 0 ? statuses : [...DEFAULT_DISBURSEMENT_STATUSES],
		),
	bookingId: z.string().catch(""),
	purchaseId: z.string().catch(""),
	/** Currency the KPI cards show when the range spans several. */
	kpiCurrency: z.string().catch(""),
});

export type PayoutsReportSearch = z.infer<typeof payoutsReportSearchSchema>;

/** The report's query: the search minus the view-only params. */
export type PayoutsReportFilters = Omit<
	PayoutsReportSearch,
	"page" | "pageSize" | "kpiCurrency"
>;

export function validatePayoutsReportSearch(
	search: Record<string, unknown>,
): PayoutsReportSearch {
	const parsed = payoutsReportSearchSchema.parse(search);
	return {
		...parsed,
		...normalizeReportRange(parsed, defaultPayoutsReportSearch()),
	};
}

/** Strips the view-only params so they never end up in a query key. */
export function toPayoutsReportFilters({
	page: _page,
	pageSize: _pageSize,
	kpiCurrency: _kpiCurrency,
	...filters
}: PayoutsReportFilters &
	Partial<
		Pick<PayoutsReportSearch, "page" | "pageSize" | "kpiCurrency">
	>): PayoutsReportFilters {
	return filters;
}
